import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'rsc-dash.favorites';
export const HA_LABEL_FAV = 'rsc-fav';

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent('rsc-dash-favorites'));
  } catch {
    /* ignore */
  }
}

/**
 * Favoriten-Store: merge aus HA-Labels (permanent pro Anlage) + localStorage (User-lokal).
 * @param labeledIds - entity_ids die per HA-Label `rsc-fav` markiert sind.
 * toggle() aendert nur die localStorage-Seite; HA-Labels muessen im HA-UI/API gesetzt werden.
 */
export function useFavorites(labeledIds: string[] = []) {
  const [localIds, setLocalIds] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const refresh = () => setLocalIds(readFavorites());
    window.addEventListener('storage', refresh);
    window.addEventListener('rsc-dash-favorites', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('rsc-dash-favorites', refresh);
    };
  }, []);

  const ids = [...new Set([...labeledIds, ...localIds])];

  const toggle = useCallback((id: string) => {
    const current = readFavorites();
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    writeFavorites(next);
    setLocalIds(next);
  }, []);

  const has = useCallback((id: string) => ids.includes(id), [ids]);

  const clear = useCallback(() => {
    writeFavorites([]);
    setLocalIds([]);
  }, []);

  return { ids, toggle, has, clear };
}
